import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGenerateKidDocument } from '../../../../../api/kids/hooks/mutations';
import {
  useGetKidData,
  useGetKidMarkerAll,
} from '../../../../../api/kids/hooks/queries';
import { useGetClient } from '../../../../../api/client';
import { KidTemplateType } from '../../../../../api/kids/types';
import { clientNameSelector } from '../../../../../redux/auth/selectors';
import { cn } from '../../../../../utilities/cn';
import type {
  FormatOptions,
  KidDocumentForm,
  PdfElement,
} from '../../../raidr/kidsV2/types/KidPdfGeneratorV2.types';
import {
  buildTemplatePdfElements,
  type SelectableKidTemplate,
} from '../../../raidr/kidsV2/components/form/templateSelection.utils';
import { transformKidDataModelsToElements } from '../../../raidr/kidsV2/utils/kidDataTransformers';
import DocumentPdfPreview from '../../../raidr/kidsV2/components/form/DocumentPdfPreview';
import {
  formatKidTemplateFundId,
  formatKidTemplateFundName,
  formatKidTemplateShareClassName,
  KidTemplateDocumentRow,
} from '../templateDocumentRows.utils';
import { applyEditedTemplateToDocumentElements } from '../templateManagement.utils';
import { sanitiseFileName } from '../../../../../utilities/helpers/common/sanitisers';

interface TemplateEditDocumentPreviewProps {
  clientId: string;
  templateId: string;
  templateName?: string;
  content: string;
  formatOptions: FormatOptions;
  templateType: KidTemplateType | null;
  /** Document to preview the template inside (required for in-document preview). */
  document?: KidTemplateDocumentRow | null;
  className?: string;
}

const stripHtml = (value: string): string =>
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

const TemplateEditDocumentPreview: FC<TemplateEditDocumentPreviewProps> = ({
  clientId,
  templateId,
  templateName,
  content,
  formatOptions,
  templateType,
  document = null,
  className,
}) => {
  const clientName = useSelector(clientNameSelector);
  const generateKidDocumentMutation = useGenerateKidDocument();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const formatOptionsKey = useMemo(
    () => JSON.stringify(formatOptions ?? {}),
    [formatOptions],
  );
  const hasContent = stripHtml(content ?? '').length > 0;

  const selectedDocument = document;
  const isDocumentPreview = !!selectedDocument;

  const { data: clientData } = useGetClient(clientId, {
    enabled: isDocumentPreview && !!clientId,
  });

  const {
    data: kidData,
    isLoading: isKidDataLoading,
    isError: isKidDataError,
  } = useGetKidData(
    clientId,
    selectedDocument?.fund_id ?? '',
    selectedDocument?.share_class_id ?? '',
    selectedDocument?.document_language ?? '',
  );

  const {
    data: markers,
    isLoading: isMarkersLoading,
    isError: isMarkersError,
  } = useGetKidMarkerAll(
    clientId,
    selectedDocument?.fund_id,
    selectedDocument?.share_class_id,
    selectedDocument?.kid_figure_calculation_date,
    selectedDocument?.last_updating_datetime,
    undefined,
    { enabled: isDocumentPreview && !!selectedDocument },
  );

  const baseElements = useMemo<PdfElement[]>(
    () =>
      isDocumentPreview && kidData
        ? transformKidDataModelsToElements(kidData as any[])
        : [],
    [isDocumentPreview, kidData],
  );

  const documentPreviewElements = useMemo(() => {
    if (!isDocumentPreview || !templateId) return [];
    const { elements } = applyEditedTemplateToDocumentElements(
      baseElements,
      templateId,
      content,
      formatOptions,
    );
    return elements;
  }, [baseElements, content, formatOptions, isDocumentPreview, templateId]);

  const templateFoundInDocument = useMemo(() => {
    if (!isDocumentPreview) return true;
    return applyEditedTemplateToDocumentElements(
      baseElements,
      templateId,
      content,
      formatOptions,
    ).templateFound;
  }, [baseElements, content, formatOptions, isDocumentPreview, templateId]);

  const markerLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    (markers || []).forEach((marker) => {
      lookup.set(`${marker.marker_type}:${marker.placeholder}`, marker.value);
    });
    return lookup;
  }, [markers]);

  const resolveMarkerTokens = useCallback(
    (value: unknown): unknown => {
      if (typeof value === 'string') {
        return value.replace(
          /\[\[([A-Z_]+):([a-z0-9_]+)\]\]/g,
          (match, markerType, placeholder) =>
            markerLookup.get(`${markerType}:${placeholder}`) || match,
        );
      }
      if (Array.isArray(value)) {
        return value.map((item) => resolveMarkerTokens(item));
      }
      return value;
    },
    [markerLookup],
  );

  const setPreviewUrl = useCallback((nextUrl: string | null) => {
    setPdfUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      objectUrlRef.current = nextUrl;
      return nextUrl;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!hasContent) {
      setPreviewUrl(null);
      generateKidDocumentMutation.reset();
      return;
    }

    if (isDocumentPreview) {
      if (!selectedDocument) {
        setPreviewUrl(null);
        return;
      }
      if (isKidDataLoading || isMarkersLoading) {
        setPreviewUrl(null);
        return;
      }
      if (isKidDataError || isMarkersError) {
        return;
      }
    }

    const timer = setTimeout(async () => {
      try {
        let payload: KidDocumentForm;

        if (isDocumentPreview && selectedDocument) {
          payload = {
            client:
              clientData?.client_name_full ||
              clientData?.client_name ||
              clientName ||
              '',
            fund_name: formatKidTemplateFundId(selectedDocument),
            fund_name_full: formatKidTemplateFundName(selectedDocument),
            share_class: formatKidTemplateShareClassName(selectedDocument),
            date:
              selectedDocument.kid_figure_calculation_date ||
              new Date().toISOString().split('T')[0],
            elements: documentPreviewElements.map((element) => ({
              ...element,
              content: resolveMarkerTokens(element.content) as PdfElement['content'],
            })),
          };
        } else {
          const template: SelectableKidTemplate = {
            template_id: templateId || 'template-preview',
            template_name: templateName || 'Template',
            template_type: templateType ?? KidTemplateType.MANCO,
            template_content: content,
            reference_id: null,
            format_options: (formatOptions ?? null) as Record<string, unknown> | null,
            rendered_content: content,
          };

          const elements = buildTemplatePdfElements([template]);
          if (!elements.length) return;

          payload = {
            client: clientName || '',
            fund_name: '',
            fund_name_full: '',
            share_class: '',
            date: new Date().toISOString(),
            elements,
          };
        }

        const response = await generateKidDocumentMutation.mutateAsync(payload);
        if (cancelled) return;

        const blob = new Blob([response.data], { type: 'application/pdf' });
        setPreviewUrl(URL.createObjectURL(blob));
      } catch (error) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to generate template preview PDF', error);
        }
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasContent,
    isDocumentPreview,
    selectedDocument,
    documentPreviewElements,
    clientData,
    clientName,
    resolveMarkerTokens,
    isKidDataLoading,
    isMarkersLoading,
    isKidDataError,
    isMarkersError,
    content,
    formatOptionsKey,
    templateId,
    templateType,
    templateName,
  ]);

  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  const isPending =
    generateKidDocumentMutation.isPending ||
    (isDocumentPreview && (isKidDataLoading || isMarkersLoading));
  const isError =
    generateKidDocumentMutation.isError ||
    (isDocumentPreview && (isKidDataError || isMarkersError));

  const showWarningBanner =
    isDocumentPreview && selectedDocument && !templateFoundInDocument;

  const downloadFileName = useMemo(() => {
    if (!selectedDocument) {
      return sanitiseFileName(templateName || 'Document', 'Document');
    }

    const fundNameLong = sanitiseFileName(
      formatKidTemplateFundName(selectedDocument),
      'fund',
    );
    const isin = sanitiseFileName(
      selectedDocument.share_class_isin?.trim() ||
        formatKidTemplateShareClassName(selectedDocument),
      selectedDocument.share_class_id,
    );

    return [fundNameLong, isin].filter(Boolean).join(' - ');
  }, [selectedDocument, templateName]);

  return (
    <div className={`flex min-h-0 flex-col ${className ?? ''}`}>
      {showWarningBanner ? (
        <div className="shrink-0 border border-b-0 border-gray-200 bg-white px-4 py-2">
          <p className="text-[11px] leading-snug text-amber-800">
            Could not find this template block in the document layout. Showing your
            edits appended at the end.
          </p>
        </div>
      ) : null}
      <DocumentPdfPreview
        previewUrl={pdfUrl}
        isPending={isPending}
        isError={isError}
        title={null}
        fileName={downloadFileName}
        className={cn(
          'min-h-0 flex-1',
          showWarningBanner && 'rounded-t-none border-t-0',
        )}
        loadingMessage={
          isDocumentPreview && (isKidDataLoading || isMarkersLoading)
            ? 'Loading document…'
            : 'Generating preview…'
        }
        errorMessage={
          isDocumentPreview && (isKidDataError || isMarkersError)
            ? 'Could not load the selected document.'
            : 'Failed to generate template preview.'
        }
        emptyMessage="Add content to see a live preview of this template."
      />
    </div>
  );
};

export default TemplateEditDocumentPreview;
